/**
 * One chat turn from the panel's side (MAR-948, out of `ChatPanel.vue`): sending, the silent client
 * tools the turn emits, an edit-and-resend, and the user's answer to a confirm card.
 */
import type { Ref } from 'vue'
import type { ChatImage } from '~/types/web-chat'
import type { ViewState } from '~~/shared/web-chat/viewState'
import type { SilentId, SilentResult } from '~/composables/web-chat/dispatch'
import type { useWebChatStore } from '~/stores/web-chat'
import type { useDocumentsStore } from '~/stores/documents'
import { useCasesStore } from '~/stores/cases'
import { acceptConfirmCard } from '~/composables/web-chat/acceptConfirmCard'
import { isContentRoute } from '~/composables/usePanelLayout'
import type { RouteQuery } from '~/composables/web-chat/useDocPreview'

type WebChat = ReturnType<typeof useWebChatStore>
type Documents = Pick<ReturnType<typeof useDocumentsStore>, 'deleteItems' | 'moveItems' | 'renameItem' | 'ensureFolder' | 'refreshAll'>
type Cases = Pick<ReturnType<typeof useCasesStore>, 'addCaseDocument'>

export interface ChatTurnDeps {
  webChat: WebChat
  documents: Documents
  /** Filing a document to a case links it here (MAR-988). Defaults to the cases store. */
  cases?: Cases
  /** The composer's text — cleared the moment a send is accepted. */
  chatInput: Ref<string>
  route: { path: string; query: RouteQuery }
  router: { replace(to: string | { query: RouteQuery }): unknown }
  silentDispatch: Record<SilentId, (input: any) => SilentResult>
  currentViewState: () => ViewState
  /** Sending settles the scope for the turn — the scope browser closes. */
  closeScopePanel: () => void
  /** Sending is a "follow the conversation" intent — re-attach to the bottom of the thread. */
  scrollToBottom: () => void
}

export function useChatTurn(deps: ChatTurnDeps) {
  const { webChat, documents, chatInput, route, router, silentDispatch } = deps

  // The turn a new send must wait for. The composer's `busy` follows the STREAM, so it takes an Enter
  // while the last turn is still settling (its answer being saved, a silent tool resuming); that send
  // used to be dropped after the composer had already cleared itself (MAR-955, DET-CHAT-066).
  let turnSettled: Promise<void> = Promise.resolve()

  async function handleSend(text?: string, images?: ChatImage[]) {
    const content = (text || chatInput.value).trim()
    if (!content && !images?.length) return
    if (webChat.attachmentsProcessing) return // a pinned document is still processing; the button is disabled too
    chatInput.value = ''
    // Re-attach to the bottom at the moment of SENDING, before waiting on the previous turn: a user
    // who scrolled up to read during that wait made a newer gesture than this send (MAR-955).
    deps.scrollToBottom()
    const previous = turnSettled
    let settle!: () => void
    turnSettled = new Promise(resolve => { settle = resolve })
    await previous
    deps.closeScopePanel()

    try {
      const isNewConversation = !webChat.conversationId
      // The typed UI state (gates client tools + rendered into the agent thread). Attaching a document
      // IS setting the id list (MAR-125), so nothing else needs syncing before the send.
      webChat.setView(deps.currentViewState())
      const attachments = webChat.contextDocuments.length > 0 ? webChat.contextDocuments.map(d => ({ ...d })) : undefined
      let { toolCalls } = await webChat.sendMessage(content, attachments, images?.length ? { images } : undefined)

      // Name the new conversation in the URL. Never by navigating to /app/c/[id] while a PAGE IS
      // SHOWING — that closes the page (MAR-492); `?chat=` on the current path keeps it open and
      // reloadable instead. `isContentRoute` is the one derivation of "is a page showing".
      if (isNewConversation && webChat.conversationId) {
        if (isContentRoute(route.path)) router.replace({ query: { ...route.query, chat: webChat.conversationId } })
        else router.replace(`/app/c/${webChat.conversationId}`)
      }

      // Silent client tools run in the UI once the stream completes, and their results resume the
      // paused ADK session; loop while the resumed turn emits more. Awaited, and a handler's own
      // return value is what goes back: a scope change must have LANDED before the resume posts (the
      // body carries the active scope), and a tool that can fail reports the failure.
      while (true) {
        const results: Array<{ id: string; name: string; response: unknown }> = []
        for (const tool of toolCalls) {
          const outcome = await silentDispatch[tool.name as SilentId]?.(tool.input)
          if (tool.id) results.push({ id: tool.id, name: tool.name, response: outcome ?? { ok: true } })
        }
        if (results.length === 0) break
        toolCalls = await webChat.resumeWithClientToolResults(results)
      }
    } finally {
      settle()
    }
  }

  async function handleEditResend(messageId: string, content: string) {
    // Awaited: the truncate DELETE must land before the resend writes rows it would otherwise sweep up.
    await webChat.truncateFrom(messageId)
    await handleSend(content)
  }

  /**
   * The user accepted the card this turn is showing — ONE handler for every confirm tool.
   * `acceptConfirmCard` settles it, runs the tool's real effect, and resumes the paused session with
   * what actually happened, failures included (MAR-957). `run_workflow` and `convert_document` own
   * their own transitions and resume, so they settle there.
   */
  async function acceptCard() {
    const pending = webChat.pendingCard()
    // Named, not silent: a confirm button that returns without a word reads as a broken one.
    if (!pending) {
      console.warn('[webChat] card accept: no card is awaiting a decision (it was already settled, or the thread reloaded without it).')
      return
    }
    const { card } = pending
    if (card.toolId === 'run_workflow') return webChat.acceptWorkflow()
    if (card.toolId === 'convert_document') return webChat.acceptConversion(card.request)

    await acceptConfirmCard(card, {
      settleCard: webChat.settleCard,
      resolveConfirmToolCall: webChat.resolveConfirmToolCall,
      deleteItems: documents.deleteItems,
      moveItems: documents.moveItems,
      renameItem: async (id, name) => { await documents.renameItem(id, name) },
      createFolder: async (name, path) => {
        const folder = await documents.ensureFolder(name, path)
        await documents.refreshAll()
        return { id: folder.id, name: folder.name }
      },
      // Resolved on accept, not at setup: the cases store is only needed once a filing is confirmed.
      linkToCase: async (caseId, itemId) => { await (deps.cases ?? useCasesStore()).addCaseDocument(caseId, itemId) },
      clearScope: () => webChat.setScope({ kind: 'global' }),
    })
  }

  /** The user declined. The agent is told the real outcome, so it does not narrate one. */
  async function declineCard() {
    const pending = webChat.pendingCard()
    if (!pending) {
      console.warn('[webChat] card decline: no card is awaiting a decision (it was already settled, or the thread reloaded without it).')
      return
    }
    const { card } = pending
    if (card.toolId === 'run_workflow') return webChat.declineWorkflow()

    webChat.settleCard(card, 'cancelled')
    const response = card.toolId === 'clear_chat_scope' ? { declined: true, scopeKept: true } : { declined: true }
    await webChat.resolveConfirmToolCall(card, response)
  }

  return { handleSend, handleEditResend, acceptCard, declineCard }
}
